import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';

const UserAutoComplete = ({
  users = [],
  name,
  value,
  size,
  error,
  helperText,
  required,
  fullWidth,
  disabled,
  onChange,
  onBlur,
  label = 'Users',
  sx = [],
  multiple = false, // Multi-select toggle
  onSelectedChange = null,
  variant,
  hiddenLabel = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (users.length > 0) {
      const formattedOptions = users.map(user => ({
        label: `${user.first_name} ${user.last_name}`,
        id: user.id,
        groups: user.groups ?? [],
      }));
      setOptions(formattedOptions);
    }
  }, [users]);

  const selectedOptions = useMemo(() => {
    if (multiple) {
      return options.filter(opt => value?.includes(opt.id)) ?? [];
    }
    return options.find(opt => opt.id === value) ?? null;
  }, [options, value, multiple]);

  useEffect(() => {
    if (onSelectedChange) {
      onSelectedChange(selectedOptions);
    }
  }, [selectedOptions, onSelectedChange]);

  return (
    <Autocomplete
      fullWidth={fullWidth}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      disablePortal
      loading={isLoading}
      value={selectedOptions}
      inputValue={inputValue}
      disabled={disabled}
      options={options}
      filterOptions={(options, state) =>
        options.filter(opt => opt.label.toLowerCase().includes(state.inputValue.toLowerCase()))
      }
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      getOptionLabel={option => option?.label ?? ''}
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
      onBlur={onBlur}
      onChange={(e, newValue) => {
        e.preventDefault();
        e.stopPropagation();
        const selectedIds = multiple ? newValue.map(opt => opt.id) : newValue?.id;
        onChange(
          {
            ...e,
            target: { id: `${name}-autocomplete-input`, name, value: selectedIds },
          },
          selectedIds,
          newValue,
        );
      }}
      onInputChange={(e, value) => {
        setInputValue(value);
      }}
      renderOption={(props, option) => (
        <Box
          {...props}
          key={option.id}
          component="li"
          sx={{ padding: '8px', borderBottom: '1px solid var(--border-default)' }}
        >
          <Typography variant="body1">
            <Highlighter searchWords={[inputValue]} autoEscape textToHighlight={option.label} />
          </Typography>
        </Box>
      )}
      renderInput={params => (
        <TextField
          id={`${name}-autocomplete-input`}
          {...params}
          required={required}
          fullWidth={fullWidth}
          name={name}
          size={size}
          variant={variant}
          hiddenLabel={hiddenLabel}
          label={hiddenLabel ? undefined : label}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: isLoading ? <CircularProgress color="inherit" size={20} /> : params.InputProps.endAdornment,
          }}
        />
      )}
    />
  );
};

export default UserAutoComplete;
